import { CardContent, CardShell } from '@/components/cards/common';
import { useFilterContext, useDerivedNetworkContext } from '@/context';
import { twMerge } from 'tailwind-merge';
import { SocialChannelProfileItem } from './SocialChannelProfile';
import { SocialProfile } from '@/types/api/social-profiles';
import useFetchQuery from '@/hooks/useFetchQuery';
import { useProfilesQuery } from '@/services/revenueApi';
import { ProfilesResponse } from '@/types/api/profile';
import { ConnectProfilesMessage } from './messages/ConnectProfilesMessage';
import NoDataMessage from '@/components/states/NoDataMessage';
import { Platform } from '@/types/api/platforms';
import { getPlatformLogoSrc } from '@/utils';

const FALLBACK_SHOW_IMAGE = getPlatformLogoSrc('Acast')!;

const SocialProfilesCard = ({
  className,
  hideProfileBanner,
}: {
  className?: string;
  hideProfileBanner?: boolean;
}) => {
  const { filters } = useFilterContext();
  const { queryResult } = useFetchQuery(useProfilesQuery, {
    enabled: filters.selectedShows.length > 0,
  });

  return (
    <CardShell className={className} headerClassName="mb-0">
      <CardContent
        {...queryResult}
        renderData={(profileData: ProfilesResponse) => (
          <Data
            data={profileData}
            selectedShow={filters.selectedShows[0]}
            hideProfileBanner={hideProfileBanner}
          />
        )}
      />
    </CardShell>
  );
};

const Data = ({
  data,
  selectedShow,
  hideProfileBanner,
}: {
  data: ProfilesResponse;
  selectedShow: string | undefined;
  hideProfileBanner?: boolean;
}) => {
  const { selectedNetwork } = useDerivedNetworkContext();

  if (!selectedNetwork.id || !selectedShow) {
    return <NoDataMessage dataType="social profile data" />;
  }

  const getFilteredProfiles = (platformName: Platform): SocialProfile[] => {
    // Filter profiles by platform and transform to SocialProfile format
    const filteredProfiles = data.profiles
      .filter((profile) => {
        // Normalize platform name comparison (case-insensitive)
        return profile.platform === platformName;
      })
      .map((profile) => {
        let handle = profile.external_id;

        if (platformName === Platform.YouTube) {
          const parts = profile.url.split('/').filter(Boolean); // last part of the URL is the handle
          handle = parts[parts.length - 1] || '';
        }

        return {
          handle,
          // Since API filters by showIds, if selectedShow is set, all profiles belong to that show
          // Otherwise, use empty string (profiles may belong to multiple shows)
          show_id: selectedShow || '',
        };
      });

    return filteredProfiles;
  };

  const acastProfiles = getFilteredProfiles(Platform.Acast);
  const youtubeProfiles = getFilteredProfiles(Platform.YouTube);
  const tiktokProfiles = getFilteredProfiles(Platform.TikTok);
  const instagramProfiles = getFilteredProfiles(Platform.Instagram);
  const totalSocialProfiles =
    youtubeProfiles.length + tiktokProfiles.length + instagramProfiles.length;
  const messageVisible = totalSocialProfiles === 0;

  return (
    <div className="flex w-full flex-row items-center overflow-hidden">
      <div
        className={twMerge(
          'flex w-full flex-row',
          messageVisible && 'flex-col lg:flex-row lg:justify-between'
        )}
      >
        <div
          className={twMerge(
            'flex w-full flex-row flex-wrap gap-x-6 gap-y-8 justify-between p-6',
            selectedShow && 'justify-start lg:gap-x-8',
            messageVisible && 'lg:w-8/12'
          )}
        >
          {selectedShow ? (
            <div className="flex flex-row w-full  items-center justify-start">
              <img
                data-testid="selected-show-image"
                className="h-28 w-28 mr-6 hidden sm:block rounded-lg"
                src={data.show?.image_url || FALLBACK_SHOW_IMAGE}
                width="104"
              />

              <div className="flex min-w-0 flex-col max-w-full gap-y-2">
                <div className="flex flex-row items-center">
                  <img
                    data-testid="selected-show-image-sm"
                    className="block sm:hidden h-8 w-8 mr-2 rounded-lg"
                    src={data.show?.image_url || FALLBACK_SHOW_IMAGE}
                    width="32"
                  />
                  <h2 className="text-textDarkGrey text-2xl md:text-[34px] font-[700] font-telegraf overflow-hidden whitespace-nowrap text-ellipsis max-w-[100%]">
                    {data.show?.name}
                  </h2>
                </div>

                <div className="flex min-w-0 flex-row gap-x-2 sm:gap-x-6">
                  <SocialChannelProfileItem type={Platform.YouTube} profiles={youtubeProfiles} />
                  <SocialChannelProfileItem type={Platform.TikTok} profiles={tiktokProfiles} />
                  <SocialChannelProfileItem
                    type={Platform.Instagram}
                    profiles={instagramProfiles}
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              <SocialChannelProfileItem type={Platform.Acast} profiles={acastProfiles} />
              <SocialChannelProfileItem type={Platform.YouTube} profiles={youtubeProfiles} />
              <SocialChannelProfileItem type={Platform.TikTok} profiles={tiktokProfiles} />
              <SocialChannelProfileItem type={Platform.Instagram} profiles={instagramProfiles} />
            </>
          )}
        </div>
        {selectedShow && totalSocialProfiles === 0 && !hideProfileBanner && (
          <ConnectProfilesMessage networkId={selectedNetwork.id} showId={selectedShow} />
        )}
      </div>
    </div>
  );
};

export default SocialProfilesCard;
